
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  Button,
  ScrollArea,
  Input,
  Label,
  Textarea,
} from "@/components/ui";

import {
  useCreateStageMutation,
  useUpdateStageMutation,
  useToast
} from "@/hooks";
import { StagesValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { StageFormProps } from "@/types";

import { MessageError } from "../../message-error";

export function FormStage({ stage, handleClose }: StageFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: createStage, isLoading: isCreating } = useCreateStageMutation();
  const { mutateAsync: updateStage, isLoading: isUpdating } = useUpdateStageMutation();
  const isLoading = isCreating || isUpdating;

  const initialValues = {
    name: stage?.name || "",
    description: stage?.description || "",
    order: stage?.order || 0,
    color: stage?.color || "#10b981",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: StagesValidationSchema(),
    onSubmit: async (values) => {
      try {
        if (stage) {
          await updateStage({
            id: stage.id,
            data: values
          });
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
        } else {
          await createStage(values);
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        }
        handleClose();
        formik.resetForm();
      } catch (error) {
        toast({
          title: t("LABEL.error"),
          description: DisplayError(error, t("MESSAGE.error_message")),
          variant: "destructive",
        });
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const colorOptions = [
    { value: "#10b981", label: "Verde", color: "#10b981" },
    { value: "#3b82f6", label: "Azul", color: "#3b82f6" },
    { value: "#f59e0b", label: "Laranja", color: "#f59e0b" },
    { value: "#ef4444", label: "Vermelho", color: "#ef4444" },
    { value: "#8b5cf6", label: "Roxo", color: "#8b5cf6" },
    { value: "#ec4899", label: "Rosa", color: "#ec4899" },
    { value: "#6b7280", label: "Cinza", color: "#6b7280" },
    { value: "#84cc16", label: "Verde Limão", color: "#84cc16" },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[50vh] md:max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2"
    >
      <ScrollArea className=" h-full">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label required>Nome</Label>
              <Input
                isInvalid={formik.touched.name && !!formik.errors.name}
                isValid={formik.touched.name && !formik.errors.name}
                messageError={formik.errors.name as string}
                {...formik.getFieldProps("name")}
              />
            </div>

            <div className="space-y-2">
              <Label>Ordem</Label>
              <Input
                type="number"
                min="0"
                isInvalid={formik.touched.order && !!formik.errors.order}
                isValid={formik.touched.order && !formik.errors.order}
                messageError={formik.errors.order as string}
                {...formik.getFieldProps("order")}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Cor</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("color")}
                  onValueChange={(value) => {
                    formik.setFieldValue("color", value);
                  }}
                  defaultValue={formik.values.color}
                >
                  <SelectTrigger
                    isInvalid={formik.touched.color && !!formik.errors.color}
                    isValid={formik.touched.color && !formik.errors.color}
                  >
                    <SelectValue placeholder="Selecione uma cor">
                      {formik.values.color && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: formik.values.color }}
                          />
                          <span>
                            {colorOptions.find(opt => opt.value === formik.values.color)?.label}
                          </span>
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {colorOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: option.color }}
                            />
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={formik.errors.color as string} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              rows={4}
              isInvalid={formik.touched.description && !!formik.errors.description}
              isValid={formik.touched.description && !formik.errors.description}
              messageError={formik.errors.description as string}
              {...formik.getFieldProps("description")}
            />
          </div>
        </div>
      </ScrollArea>

      <div className="flex items-center justify-end gap-x-6">
        <Button
          type="submit"
          className="px-10"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {stage ? t("LABEL.save") : t("LABEL.create")}
        </Button>
      </div>
    </form>
  );
}